import { useState, useEffect } from "react";
import { X, Barbell } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Read-only lightbox for the client app. Pulls the exercise definition from
 *  the (already loaded) library and lazily streams the media via the shared
 *  files endpoint. Designed to match the look of `ExercisePreviewLightbox` on
 *  the coach side. */
export default function SharedExercisePreview({ exercise, libraryEntry, token, onClose }) {
  const [mediaUrl, setMediaUrl] = useState(null);
  const mediaPath = libraryEntry?.media_path;

  useEffect(() => {
    if (!mediaPath || !token) return undefined;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await fetch(`${API}/shared/${token}/files/${mediaPath}`);
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaUrl(objectUrl);
      } catch (err) {
        console.debug("[SharedExercisePreview] media fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaPath, token]);

  if (!exercise) return null;

  const secondaries = Array.isArray(libraryEntry?.secondary_muscle_groups) && libraryEntry.secondary_muscle_groups.length > 0
    ? libraryEntry.secondary_muscle_groups
    : (libraryEntry?.secondary_muscle_group ? [libraryEntry.secondary_muscle_group] : []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="shared-exercise-preview"
    >
      <div
        className="max-w-md w-full bg-[#0F0F0F] border border-[#262626] rounded-xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white/80 hover:text-white z-10"
          data-testid="shared-exercise-preview-close"
        >
          <X size={16} />
        </button>

        {/* Media (fallback to a barbell icon) */}
        <div className="aspect-[16/9] bg-[#0A0A0A] flex items-center justify-center">
          {mediaUrl ? (
            <img src={mediaUrl} alt={exercise.name} className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-zinc-700">
              <Barbell size={36} weight="duotone" />
              <p className="text-[10px] uppercase tracking-wider">Nessun media</p>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4">
          <h3 className="text-base font-bold text-white">{exercise.name}</h3>
          {libraryEntry && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {libraryEntry.category && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#007AFF]/10 text-[#007AFF] rounded-full font-medium">
                  {libraryEntry.category}
                </span>
              )}
              {libraryEntry.muscle_group && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#D4FF00]/10 text-[#D4FF00] rounded-full font-medium">
                  {libraryEntry.muscle_group}
                </span>
              )}
              {secondaries.map((sg) => (
                <span key={`sg-${sg}`} className="text-[10px] px-1.5 py-0.5 bg-[#34C759]/10 text-[#34C759] rounded-full font-medium">
                  {sg}
                </span>
              ))}
              {libraryEntry.equipment && (
                <span className="text-[10px] px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 rounded-full font-medium">
                  {libraryEntry.equipment}
                </span>
              )}
            </div>
          )}
          {libraryEntry?.description && (
            <div className="mt-3 p-2.5 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">
                Esecuzione
              </p>
              <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {libraryEntry.description}
              </p>
            </div>
          )}
          {!libraryEntry && (
            <p className="text-[11px] text-zinc-500 mt-3">
              Questo esercizio non è collegato alla libreria. Chiedi al tuo coach di mapparlo per vederne i dettagli.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
